/**
 * CollectionTracker - Tracks user navigation from collections to products
 * Designed specifically for Shopify sites
 */
class CollectionTracker {
  constructor() {
    // Storage keys
    this.keys = {
      collection: 'lastViewedCollection',
      timestamp: 'collectionViewTimestamp',
      url: 'lastCollectionUrl'
    };
    
    // Configuration
    this.config = {
      maxJourneyTimeMs: 30000, // 30 seconds
      cleanupTimeMs: 3600000,  // 1 hour
      breadcrumbSelector: '.product-collection-breadcrumb',
      hiddenClass: 'hidden'    // The hidden class to remove
    };
    
    this.initialize();
  }
  
  initialize() {
    // Determine page type and perform appropriate action
    if (this.isCollectionPage()) {
      this.handleCollectionPage();
    } else if (this.isProductPage()) {
      this.handleProductPage();
    }
    
    // Set up cleanup
    this.setupCleanup();
  }
  
  isCollectionPage() {
    return window.location.pathname.includes('/collections/') && 
           !window.location.pathname.includes('/products/');
  }
  
  isProductPage() {
    return window.location.pathname.includes('/products/');
  }
  
  handleCollectionPage() {
    const collectionName = this.extractCollectionName();
    if (collectionName) {
      this.storeCollectionData(collectionName);
    }
  }
  
  extractCollectionName() {
    try {
      const pathname = window.location.pathname;
      const collectionPath = pathname.split('/collections/')[1].split('/')[0];
      
      // Handle Shopify's collection naming conventions
      return decodeURIComponent(collectionPath)
        .replace(/-/g, ' ')
        .trim();
    } catch (error) {
      console.warn('Unable to extract collection name:', error);
      return null;
    }
  }
  
  storeCollectionData(collectionName) {
    localStorage.setItem(this.keys.collection, collectionName);
    localStorage.setItem(this.keys.timestamp, Date.now().toString());
    localStorage.setItem(this.keys.url, window.location.href);
  }
  
  handleProductPage() {
    // Check if user came directly from a collection
    if (this.isValidJourney()) {
      this.updateBreadcrumb();
    }else{
      const breadcrumb = document.querySelector(this.config.breadcrumbSelector);
       // Remove hidden class to reveal the breadcrumb
      if(breadcrumb){
        breadcrumb.classList.remove(this.config.hiddenClass);
      }
    }
  }
  
  isValidJourney() {
    const timestamp = localStorage.getItem(this.keys.timestamp);
    const collection = localStorage.getItem(this.keys.collection);
    const collectionUrl = localStorage.getItem(this.keys.url);
    
    // Verify we have all required data
    if (!timestamp || !collection || !collectionUrl) {
      return false;
    }
    
    // Check if journey happened within configured timeframe
    const elapsed = Date.now() - parseInt(timestamp);
    if (elapsed > this.config.maxJourneyTimeMs) {
      return false;
    }
    
    // Verify referrer matches stored collection URL
    // For Shopify, we need to be a bit flexible with the comparison
    const referrer = document.referrer;
    const referrerPath = referrer.split('?')[0].split('#')[0];
    const storedUrlPath = collectionUrl.split('?')[0].split('#')[0];
    
    return referrerPath.includes(storedUrlPath);
  }
  
  updateBreadcrumb() {
    const breadcrumb = document.querySelector(this.config.breadcrumbSelector);
    if (!breadcrumb) return;
    
    const collection = localStorage.getItem(this.keys.collection);
    
    // Store original text for potential restoration
    if (!breadcrumb.dataset.originalText) {
      breadcrumb.dataset.originalText = breadcrumb.textContent;
    }
    
    // Update text content
    breadcrumb.textContent = collection;
    
    // Remove hidden class to reveal the breadcrumb
    breadcrumb.classList.remove(this.config.hiddenClass);
  }
  
  setupCleanup() {
    window.addEventListener('beforeunload', () => {
      const timestamp = localStorage.getItem(this.keys.timestamp);
      if (timestamp && (Date.now() - parseInt(timestamp) > this.config.cleanupTimeMs)) {
        this.cleanStorage();
      }
    });
  }
  
  cleanStorage() {
    Object.values(this.keys).forEach(key => {
      localStorage.removeItem(key);
    });
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  new CollectionTracker();
});